import { isSettableKind } from "./types";
import type { ApplyOutcome, BridgeValue, DiscoveredKind, FieldAdapter, FieldDescriptor, FieldOption, FieldValue } from "./types";

type FieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const TEXT_INPUT_TYPES = new Set(["text", "search", "url", "tel", "password"]);

function isFieldElement(element: HTMLElement): element is FieldElement {
  return element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement;
}

/**
 * The wire kind a native element maps to; anything the chat cannot set meaningfully is unsupported.
 * That includes `datetime-local`: its value carries no offset, so it cannot be the wire's date-time.
 */
export function domKindOf(element: HTMLElement): DiscoveredKind {
  if (element instanceof HTMLTextAreaElement) {
    return "textarea";
  }
  if (element instanceof HTMLSelectElement) {
    return element.multiple ? "multiselect" : "select";
  }
  if (!(element instanceof HTMLInputElement)) {
    return "unsupported";
  }
  const type = element.type.toLowerCase();
  if (TEXT_INPUT_TYPES.has(type)) {
    return "text";
  }
  switch (type) {
    case "number":
    case "range":
      return "number";
    case "checkbox":
      return "checkbox";
    case "email":
      return "email";
    case "date":
      return "date";
    default:
      return "unsupported";
  }
}

// A disabled option in a multi-select is a placeholder that can never be picked (e.g. a game tag
// without a name id); a single select's disabled option is still the value it may currently show.
function selectOptions(element: HTMLSelectElement): FieldOption[] {
  return Array.from(element.options)
    .filter((option) => !(element.multiple && option.disabled))
    .map((option) => ({ value: option.value, label: option.text.trim() || option.value }));
}

function readValue(element: FieldElement, kind: DiscoveredKind): BridgeValue {
  if (element instanceof HTMLSelectElement && element.multiple) {
    return Array.from(element.options)
      .filter((option) => option.selected)
      .map((option) => option.value);
  }
  if (element instanceof HTMLInputElement && kind === "checkbox") {
    return element.checked;
  }
  if (element instanceof HTMLInputElement && kind === "number") {
    return element.value === "" ? null : Number(element.value);
  }
  return element.value;
}

function readError(element: FieldElement): string | undefined {
  if (element.classList.contains("is-invalid")) {
    return "invalid";
  }
  return element.validationMessage || undefined;
}

// React only re-renders a controlled input from the value its own tracker last saw, so the
// value has to go in through the prototype setter — the same path real typing takes.
function setNativeValue(element: HTMLInputElement | HTMLTextAreaElement, value: string): void {
  const prototype = element instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const descriptor = Object.getOwnPropertyDescriptor(prototype, "value");
  if (descriptor && descriptor.set) {
    descriptor.set.call(element, value);
  } else {
    element.value = value;
  }
}

function dispatch(element: HTMLElement, type: string): void {
  element.dispatchEvent(new Event(type, { bubbles: true }));
}

// A multi-select's change handler reads which options are selected, so each option is set
// before the one change event a user's pick would fire.
function writeSelection(element: HTMLSelectElement, value: BridgeValue): void {
  const selected = new Set(Array.isArray(value) ? value.map(String) : []);
  Array.from(element.options).forEach((option) => {
    option.selected = selected.has(option.value);
  });
  dispatch(element, "change");
}

function writeValue(element: FieldElement, kind: DiscoveredKind, value: BridgeValue): void {
  if (element instanceof HTMLSelectElement && element.multiple) {
    writeSelection(element, value);
    return;
  }
  if (element instanceof HTMLInputElement && kind === "checkbox") {
    if (element.checked !== Boolean(value)) {
      element.click();
    }
    return;
  }
  if (element instanceof HTMLSelectElement) {
    element.value = value == null ? "" : String(value);
    dispatch(element, "change");
    return;
  }
  setNativeValue(element, value == null ? "" : String(value));
  dispatch(element, "input");
  dispatch(element, "change");
}

/**
 * Describes and fills a native input, select or textarea from the DOM alone — the baseline for
 * every form framework (react-hook-form, rjsf, react-bootstrap) and the fallback for a kit input
 * whose registry handle is gone.
 */
export function createDomFieldAdapter(element: HTMLElement, path: string): FieldAdapter {
  const kind = domKindOf(element);
  const field = isFieldElement(element) ? element : undefined;

  const options = (): FieldOption[] | undefined => (field instanceof HTMLSelectElement ? selectOptions(field) : undefined);
  const currentValue = (): BridgeValue => (field ? readValue(field, kind) : undefined);

  const describe = (): FieldDescriptor => {
    const descriptor: FieldDescriptor = { path, kind: kind === "unsupported" ? "text" : kind, label: path };
    if (!field) {
      return descriptor;
    }
    const value = currentValue();
    if (value !== undefined) {
      descriptor.value = value;
    }
    const optionList = options();
    if (optionList) {
      descriptor.options = optionList;
    }
    if (field.required) {
      descriptor.required = true;
    }
    if (field.disabled || ("readOnly" in field && field.readOnly)) {
      descriptor.readOnly = true;
    }
    const error = readError(field);
    if (error) {
      descriptor.error = error;
    }
    return descriptor;
  };

  const restore = (value: BridgeValue): void => {
    if (field) {
      writeValue(field, kind, value);
    }
  };

  const apply = (value: FieldValue): ApplyOutcome => {
    if (!field || !isSettableKind(kind)) {
      return { applied: false, reason: "unsupported" };
    }
    restore(value);
    return { applied: true };
  };

  return { element, kind, options, currentValue, describe, apply, restore };
}
